import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiRequestError } from '../../../api/client'
import { isApiRecord } from '../../../api/responseDecoders'
import {
  useOnboardingEmailTemplate,
  usePreviewOnboardingEmailTemplate,
  useResetOnboardingEmailTemplate,
  useSaveOnboardingEmailTemplate,
} from '../../../api/hooks'
import type {
  OnboardingEmailTemplateDraft,
  OnboardingEmailTemplatePreview,
  OnboardingTemplateIssue,
} from '../../../api/onboardingTypes'
import { INPUT_CLASS, SECONDARY_BUTTON_CLASS, SMALL_PRIMARY_BUTTON_CLASS } from './ui'

// Per-run custom welcome email (onboarding P3).
//
// The SERVER is the control: it validates against a strict allowlist, stores a
// canonical form, re-checks the rendered email at send time, and freezes the
// template once any email in the run has been attempted. This editor only
// makes those rules visible early — a lint-as-you-type preview — and never
// decides anything itself.

// Rendered as code, never through i18n: i18next would interpolate the braces.
const BODY_TOKENS = ['{{first_name}}', '{{user_email}}', '{{temp_password}}']
const REQUIRED_TOKENS = ['{{user_email}}', '{{temp_password}}']
const SUBJECT_TOKENS = ['{{first_name}}']
const PREVIEW_DEBOUNCE_MS = 500

/** Every error in a 422 TEMPLATE_INVALID save response, or null if it is not one. */
export function templateSaveIssues(error: Error | null): OnboardingTemplateIssue[] | null {
  if (!(error instanceof ApiRequestError) || !isApiRecord(error.errorData)) return null
  const detail = error.errorData.detail
  if (!isApiRecord(detail) || detail.code !== 'TEMPLATE_INVALID' || !Array.isArray(detail.errors)) {
    return null
  }
  const issues: OnboardingTemplateIssue[] = []
  for (const item of detail.errors) {
    if (!isApiRecord(item) || typeof item.code !== 'string' || typeof item.message !== 'string') {
      return null
    }
    issues.push({
      code: item.code,
      message: item.message,
      field: typeof item.field === 'string' ? item.field : null,
      line: typeof item.line === 'number' ? item.line : null,
    })
  }
  return issues
}

function sameDraft(a: OnboardingEmailTemplateDraft, b: OnboardingEmailTemplateDraft): boolean {
  return a.subject === b.subject && a.html === b.html && a.text === b.text
}

export default function EmailTemplateEditor({
  runId,
  previewTargetId,
}: {
  runId: string
  /** Whose greeting and address the draft preview renders; the first ready recipient. */
  previewTargetId: string | null
}) {
  const { t } = useTranslation('pages')
  const template = useOnboardingEmailTemplate(runId)
  const save = useSaveOnboardingEmailTemplate()
  const reset = useResetOnboardingEmailTemplate()
  const previewDraft = usePreviewOnboardingEmailTemplate()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<OnboardingEmailTemplateDraft | null>(null)
  // The preview of exactly the draft it was rendered for; a response for an
  // older draft (typing outran the network) is discarded, not shown.
  const [preview, setPreview] = useState<{
    draft: OnboardingEmailTemplateDraft
    result: OnboardingEmailTemplatePreview
  } | null>(null)
  const latest = useRef(0)
  const { mutate: requestPreview } = previewDraft

  useEffect(() => {
    if (!open || draft === null) return
    const request = ++latest.current
    const pending = draft
    const timer = window.setTimeout(() => {
      requestPreview(
        { runId, draft: pending, targetId: previewTargetId },
        {
          onSuccess: (result) => {
            if (request === latest.current) setPreview({ draft: pending, result })
          },
        },
      )
    }, PREVIEW_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [open, draft, runId, previewTargetId, requestPreview])

  const data = template.data
  if (!data) {
    return template.error instanceof Error ? (
      <p className="text-xs font-medium text-destructive" data-testid="onboarding-template-load-error">
        {template.error.message}
      </p>
    ) : null
  }

  // A save or reset in flight locks the editor: a later edit would be discarded
  // when the save closes it, and a reset racing a save could leave the cache
  // in a different order from the server's writes.
  const changing = save.isPending || reset.isPending
  const readOnly = !data.editable
  const locked = readOnly || changing
  // With custom templates off, a run on the default has nothing to show here;
  // a run that already chose a custom email still shows it, read-only.
  if (!data.custom_templates_enabled && data.kind === 'default') return null

  const current = preview && draft && sameDraft(preview.draft, draft) ? preview.result : null
  const saveIssues = templateSaveIssues(save.error)
  const unchanged =
    draft !== null && sameDraft(draft, { subject: data.subject, html: data.html, text: data.text })
  const saveDisabled =
    readOnly ||
    draft === null ||
    current === null ||
    current.errors.length > 0 ||
    previewDraft.isPending ||
    changing ||
    (unchanged && data.kind === 'custom')
  const otherError =
    (save.error && !saveIssues && save.error instanceof Error ? save.error.message : null) ??
    (reset.error instanceof Error ? reset.error.message : null) ??
    (previewDraft.error instanceof Error ? previewDraft.error.message : null)

  const openEditor = () => {
    // Never reset a pending mutation: reset() clears isPending without
    // cancelling the request, which would unlock a racing second write.
    if (changing) return
    setDraft({ subject: data.subject, html: data.html, text: data.text })
    setPreview(null)
    save.reset()
    setOpen(true)
  }

  // A rejected save describes the draft it was given; once the operator edits,
  // that error no longer matches what is on screen, so it is cleared.
  const editDraft = (change: Partial<OnboardingEmailTemplateDraft>) => {
    if (save.error) save.reset()
    setDraft((current) => (current ? { ...current, ...change } : current))
  }

  const handleSave = () => {
    if (!draft) return
    save.mutate({ runId, draft }, { onSuccess: () => setOpen(false) })
  }

  const handleReset = () => {
    reset.mutate({ runId }, { onSuccess: () => setOpen(false) })
  }

  const issues = saveIssues ?? current?.errors ?? []

  return (
    <div className="flex flex-col gap-2 text-xs" data-testid="onboarding-template-editor">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground" data-testid="onboarding-template-kind">
          {data.kind === 'custom'
            ? t('adminOnboarding.template.custom', { who: data.updated_by ?? '—' })
            : t('adminOnboarding.template.default')}
        </span>
        {!open && (
          <button
            type="button"
            onClick={openEditor}
            disabled={changing}
            className={SECONDARY_BUTTON_CLASS}
            data-testid="onboarding-template-open"
          >
            {readOnly ? t('adminOnboarding.template.view') : t('adminOnboarding.template.customize')}
          </button>
        )}
      </div>
      {data.frozen_reason && (
        <p className="font-medium text-muted-foreground" data-testid="onboarding-template-frozen">
          {data.frozen_reason}
        </p>
      )}
      {!data.custom_templates_enabled && (
        <p className="font-medium text-muted-foreground" data-testid="onboarding-template-disabled">
          {t('adminOnboarding.template.disabled')}
        </p>
      )}

      {open && draft && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-2 p-3">
          <p className="text-muted-foreground">{t('adminOnboarding.template.rules')}</p>
          <p className="text-muted-foreground">
            {t('adminOnboarding.template.tokens')}{' '}
            {BODY_TOKENS.map((token) => (
              <code key={token} className="mr-1 rounded bg-surface-3 px-1 text-foreground">
                {token}
                {REQUIRED_TOKENS.includes(token) ? '*' : ''}
              </code>
            ))}
            <span>{t('adminOnboarding.template.tokensRequired')}</span>
          </p>

          <label className="flex flex-col gap-1">
            <span className="text-foreground">
              {t('adminOnboarding.template.subject')}{' '}
              <span className="text-muted-foreground">
                ({SUBJECT_TOKENS.join(', ')})
              </span>
            </span>
            <input
              type="text"
              value={draft.subject}
              readOnly={locked}
              onChange={(e) => editDraft({ subject: e.target.value })}
              className={INPUT_CLASS}
              data-testid="onboarding-template-subject"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-foreground">{t('adminOnboarding.template.html')}</span>
            <textarea
              value={draft.html}
              readOnly={locked}
              spellCheck={false}
              onChange={(e) => editDraft({ html: e.target.value })}
              className="h-64 rounded-lg border border-input-border bg-input-bg p-2 font-mono text-xs text-foreground focus:border-primary focus:outline-none"
              data-testid="onboarding-template-html"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-foreground">{t('adminOnboarding.template.text')}</span>
            <textarea
              value={draft.text}
              readOnly={locked}
              spellCheck={false}
              onChange={(e) => editDraft({ text: e.target.value })}
              className="h-40 rounded-lg border border-input-border bg-input-bg p-2 font-mono text-xs text-foreground focus:border-primary focus:outline-none"
              data-testid="onboarding-template-text"
            />
          </label>

          {issues.length > 0 && (
            <ul className="list-disc pl-5 text-destructive" data-testid="onboarding-template-errors">
              {issues.map((issue, index) => (
                <li key={`${issue.code}-${index}`}>
                  {issue.field ? `${issue.field}${issue.line ? `:${issue.line}` : ''} — ` : ''}
                  {issue.message}
                </li>
              ))}
            </ul>
          )}
          {current?.warnings.map((warning, index) => (
            <p
              key={`${warning.code}-${index}`}
              className="font-medium text-warning"
              data-testid="onboarding-template-warning"
            >
              {warning.message}
            </p>
          ))}
          {otherError && (
            <p className="font-medium text-destructive" data-testid="onboarding-template-action-error">
              {otherError}
            </p>
          )}

          {current === null && (
            <p className="text-muted-foreground">{t('adminOnboarding.emailPreview.loading')}</p>
          )}
          {current?.html && (
            <>
              <p className="font-medium text-foreground">
                <span className="text-muted-foreground">{t('adminOnboarding.emailPreview.subject')}</span>{' '}
                {current.subject}
              </p>
              {/* sandbox="" is the most restrictive setting: no scripts, no
                  same-origin, no forms, no navigation. The server already
                  refused anything that could fetch, but the frame must not
                  depend on that staying true. */}
              <iframe
                title={t('adminOnboarding.emailPreview.frameTitle')}
                sandbox=""
                referrerPolicy="no-referrer"
                srcDoc={current.html}
                className="h-96 w-full rounded border border-border bg-white"
                data-testid="onboarding-template-preview-frame"
              />
              <details className="font-medium text-muted-foreground">
                <summary className="cursor-pointer">{t('adminOnboarding.emailPreview.plainText')}</summary>
                <pre
                  className="mt-1 whitespace-pre-wrap rounded border border-border bg-surface-1 p-2 text-foreground"
                  data-testid="onboarding-template-preview-text"
                >
                  {current.text}
                </pre>
              </details>
            </>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {!readOnly && (
              <button
                type="button"
                disabled={saveDisabled}
                onClick={handleSave}
                className={SMALL_PRIMARY_BUTTON_CLASS}
                data-testid="onboarding-template-save"
              >
                {t('adminOnboarding.template.save')}
              </button>
            )}
            {!readOnly && data.kind === 'custom' && (
              <button
                type="button"
                disabled={changing}
                onClick={handleReset}
                className={SECONDARY_BUTTON_CLASS}
                data-testid="onboarding-template-reset"
              >
                {t('adminOnboarding.template.reset')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={changing}
              className={SECONDARY_BUTTON_CLASS}
              data-testid="onboarding-template-close"
            >
              {t('adminOnboarding.template.close')}
            </button>
            {!readOnly && (
              <span className="text-muted-foreground">{t('adminOnboarding.template.saveNote')}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
